import {
  adobeAnalyticsPageLoad,
  fireAdobeAnalyticsEvent,
  adobeCleanString,
} from '../../helpers/adobe-analytics';
import { createFormAnalytics } from '../../helpers/form-analytics';
import { createToolAnalytics } from '../../helpers/tool-analytics';

const OPACITY_LEVELS = ['I', 'II', 'III', 'IV'];
const VIEWER_EVENT = name => `bitreel-digital-print-${name}`;

const imageLabel = image => {
  const imageNumber = image?.url?.match(/IM\d+/i)?.[0];
  return imageNumber ? imageNumber.toUpperCase() : 'Library';
};

// The viewer reports its whole config; the selection is whatever changed
// since the last one. Labels match the Walter's Digital Print Designer.
const reportConfigChanges = (toolAnalytics, before, after) => {
  if (after.material !== before.material) {
    toolAnalytics.select('Material', after.material);
  }
  if (after.opacity !== before.opacity) {
    toolAnalytics.select('Opacity', OPACITY_LEVELS[after.opacity - 1]);
  }
  const layout = after.layout || {};
  const beforeLayout = before.layout || {};
  if (layout.orientation !== beforeLayout.orientation) {
    toolAnalytics.select('Layout', layout.orientation);
  } else if (layout.panels !== beforeLayout.panels) {
    // Switching to multi brings its default panel count, not a choice.
    toolAnalytics.select('Panels', layout.panels);
  }
  if (after.showProps !== before.showProps) {
    toolAnalytics.select('Props', after.showProps ? 'on' : 'off');
  }
  const image = after.image;
  const beforeImage = before.image;
  if (
    image &&
    (image.url !== beforeImage?.url || image.name !== beforeImage?.name)
  ) {
    if (image.url) toolAnalytics.select('Image', imageLabel(image));
    else toolAnalytics.upload('Image');
  }
};

// Viewers before the analytics events send only quote and download events,
// so Tool View comes from the element and everything else stays silent.
const setupDigitalPrintToolAnalytics = () => {
  const toolAnalytics = createToolAnalytics('Digital Print Configurator');
  const viewer = document.querySelector(
    '#bitreel-digital-print bitreel-fiber-viewer'
  );
  toolAnalytics.observe(viewer);

  // The viewer also settles its own config as an image loads (orientation
  // from the image's metadata), so changes only count after a first touch.
  let touched = false;
  const markTouched = () => {
    touched = true;
  };
  viewer?.addEventListener('pointerdown', markTouched, { passive: true });
  viewer?.addEventListener('keydown', markTouched, { passive: true });

  let config = null;
  document.addEventListener(VIEWER_EVENT('ready'), event => {
    config = event.detail?.config || null;
  });
  document.addEventListener(VIEWER_EVENT('config-changed'), event => {
    const next = event.detail || {};
    if (config && touched) reportConfigChanges(toolAnalytics, config, next);
    config = next;
  });
  document.addEventListener(VIEWER_EVENT('view-changed'), event =>
    toolAnalytics.viewChanged(event.detail)
  );
  document.addEventListener(VIEWER_EVENT('editor-placement-changed'), event =>
    toolAnalytics.placementChanged(event.detail)
  );
  ['download-configured-image', 'download-source-image'].forEach(name =>
    document.addEventListener(VIEWER_EVENT(name), () =>
      toolAnalytics.activity()
    )
  );
  return toolAnalytics;
};

// Recursive schema-to-source mapper
const mapBySchema = (source, schema) => {
  if (typeof schema === 'function') {
    const value = schema(source);
    return value === undefined ? '' : value;
  }

  if (schema && typeof schema === 'object' && !Array.isArray(schema)) {
    return Object.fromEntries(
      Object.entries(schema).map(([k, v]) => [k, mapBySchema(source, v)])
    );
  }

  if (typeof schema === 'string') {
    const value = schema in source ? source[schema] : undefined;
    return value === undefined ? '' : value;
  }

  // do not allow undefined values, only empty strings
  return schema === undefined ? '' : schema;
};

const digitalPrintLeadsSchema = () => {
  const schema = {
    input: {
      leadAttributes: {
        firstName: 'firstName',
        lastName: 'lastName',
        company: 'companyName',
        email: 'email',
        phone: 'phone',
        zipcode: 'zipCode',
        country: 'country',
        projectName: 'projectName',
      },
      visualizationAttributes: {
        configurationUrl: 'configurationUrl',
        materialType: 'material',
        opacity: source => String(source.opacity),
        panelOrientation: 'orientation',
        customInstructions: 'projectDetails',
        imageName: 'threeformImageName',
        configuredImageUrl: 'configuredImageUrl',
        imageUrl: 'threeformImageUrl',
        customerSuppliedImageUrl: 'customerSuppliedImageUrl',
      },
    },
  };
  return schema;
};

const setupDigitalPrintEventListener = () => {
  // The quote form lives in Bitreel's frame, so its typing isn't visible
  // here; opening it counts as the view and the lead post as the submit.
  const formAnalytics = createFormAnalytics('Contact|Digital Print Quote');
  const toolAnalytics = setupDigitalPrintToolAnalytics();

  const postDigitalPrintLead = async payload => {
    try {
      const response = await fetch('/add-marketing-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded ${response.status}: ${errorText}`);
      }

      const json = await response.json();
      console.info('Lead successfully submitted:', json);
      return json;
    } catch (err) {
      console.error('Failed to submit digital-print lead:', err);
      throw err;
    }
  };

  const fireDigitalPrintAnalytics = (action, event) => {
    const imageSource = !!event.detail?.customerSuppliedImage
      ? 'Customer Image'
      : '3form Image';
    const eventData = {
      type: 'click',
      detail: {
        eVar8: `INP|${action}|${imageSource}`,
      },
    };
    console.log('fireDigitalPrintAnalytics:', eventData);
    fireAdobeAnalyticsEvent(eventData);
  };

  document.addEventListener(
    'bitreel-digital-print-quote-submitted',
    async event => {
      toolAnalytics.flush();
      const formData = event.detail?.formData || {};
      const mapped = mapBySchema(formData, digitalPrintLeadsSchema()).input;

      // hard coded values required by My3form specific for digital print
      mapped.leadAttributes['source'] = 'digital_print_visualization_lead';
      mapped.visualizationAttributes['leadType'] = 'Digital Print Lead';

      // sometimes configurationUrl is undefined from Bitreel which causes the
      // push to the lead dashboard to fail. This creates a default fallback.
      mapped.visualizationAttributes['configurationUrl'] =
        mapped.visualizationAttributes.configurationUrl ||
        'https://3-form.com/digital-printing/configure';

      const success = await postDigitalPrintLead(mapped)
        .then(() => true)
        .catch(() => false);

      document.dispatchEvent(
        new CustomEvent('digital-print-quote-submit-complete', {
          detail: { success },
        })
      );

      // A failed lead never reached the dashboard, so it is not a submission.
      if (success) {
        fireDigitalPrintAnalytics('RFQ Submitted', event);
        formAnalytics.submit();
      } else {
        formAnalytics.error();
      }
    }
  );

  document.addEventListener(
    'bitreel-digital-print-request-quote-opened',
    event => {
      fireDigitalPrintAnalytics('Open RFQ Modal', event);
      formAnalytics.view();
      toolAnalytics.action(
        'Request a Quote',
        event.detail?.customerSuppliedImage ? 'Customer Image' : '3form Image'
      );
    }
  );
};

export default {
  init: setupDigitalPrintEventListener,
};
